import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  UserPlus, CheckCircle2, XCircle, Archive, Search, ShieldAlert, RefreshCw, Trash2,
} from "lucide-react";
import { ROLES } from "@/lib/roles";

const ADMIN_ROLES = ["super_admin", "founder", "hr_admin"];

const STATUS_TABS = [
  { key: "email_verification_pending", label: "Verify email" },
  { key: "pending_approval",           label: "Pending review" },
  { key: "approved",                   label: "Approved" },
  { key: "rejected",                   label: "Rejected" },
  { key: "archived",                   label: "Archived" },
];

const STATUS_BADGE = {
  email_verification_pending: "bg-yellow-100 text-yellow-700",
  pending_approval:           "bg-orange-100 text-orange-700",
  pending:                    "bg-orange-100 text-orange-700",  // legacy
  approved:                   "bg-green-100 text-green-700",
  rejected:                   "bg-red-100 text-red-700",
  archived:                   "bg-slate-200 text-slate-600",
};

export default function AccessRequestsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [tab, setTab] = useState("pending_approval");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [params, setParams] = useSearchParams();
  const highlightId = params.get("highlight");

  const isAdmin = user && ADMIN_ROLES.includes(user.role);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/access-requests", { params: { status: tab, q: q || undefined } }),
        api.get("/access-requests/summary"),
      ]);
      setRows(r.data);
      setCounts(s.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load requests");
    } finally { setLoading(false); }
  }, [isAdmin, tab, q]);

  useEffect(() => { load(); }, [load]);

  // Deep-link: /admin/access-requests?highlight=<id> — jump to that request
  // and auto-open the approve dialog if it is still pending. Triggered by
  // "Approve in MIRO One" button in the admin notification email.
  useEffect(() => {
    if (!highlightId || !isAdmin) return;
    (async () => {
      try {
        // Pull all statuses to find the highlighted request across tabs.
        const r = await api.get("/access-requests");
        const match = r.data.find((x) => x.id === highlightId);
        if (!match) return;
        if (match.status !== tab) setTab(match.status);
        if (match.status === "pending_approval" || match.status === "pending") setApproveTarget(match);
        // Clear the query param once we've acted on it.
        params.delete("highlight");
        setParams(params, { replace: true });
      } catch (err) {
        // Deep-link navigation is a nice-to-have; log so devs can see the
        // reason without spamming the user's toast on a failed helper call.
        if (process.env.NODE_ENV !== "production") {
          console.warn("access-request highlight failed:", err);
        }
      }
    })();
    // eslint-disable-next-line
  }, [highlightId, isAdmin]);

  const archive = async (r) => {
    if (!window.confirm(`Archive request from ${r.email}?`)) return;
    try {
      await api.post(`/access-requests/${r.id}/archive`);
      toast.success("Request archived");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Archive failed"); }
  };

  const remove = async (r) => {
    if (!window.confirm(
      `Permanently DELETE access request from ${r.email}?\n\n` +
      `This removes the request row entirely. If a user account was ` +
      `already created via 'Approve', that account is NOT deleted.\n\n` +
      `This cannot be undone.`
    )) return;
    try {
      await api.delete(`/access-requests/${r.id}`);
      toast.success("Request removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  if (!isAdmin) {
    return (
      <AppLayout title="Access Requests">
        <div data-testid="access-requests-denied" className="max-w-md mx-auto mt-16 miro-card p-8 text-center">
          <ShieldAlert className="mx-auto mb-3 text-red-500" size={30}/>
          <div className="font-display font-black text-xl text-[var(--miro-navy)]">Access denied</div>
          <div className="text-sm text-[var(--miro-text-secondary)] mt-1">
            Only Super Admin, Founder or HR Admin can review access requests.
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Access Requests">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-1 bg-white border border-[var(--miro-border)] rounded-lg p-1">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`ar-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
                tab === t.key
                  ? "bg-[var(--miro-navy)] text-white"
                  : "text-[var(--miro-text-secondary)] hover:bg-slate-50"
              }`}
            >
              {t.label}
              <span className="ml-1.5 text-[10px] opacity-70">
                {counts?.[t.key] ?? 0}
              </span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 bg-white border border-[var(--miro-border)] rounded-lg px-3 py-2 flex-1 min-w-[220px] max-w-md">
          <Search size={15} className="text-[var(--miro-text-muted)]"/>
          <input data-testid="ar-search"
                 className="bg-transparent outline-none text-sm flex-1"
                 placeholder="Search name, email, role…"
                 value={q} onChange={(e) => setQ(e.target.value)}/>
        </div>
        <Button data-testid="ar-refresh" variant="outline" size="sm" onClick={load}>
          <RefreshCw size={13} className="mr-1"/> Refresh
        </Button>
      </div>

      <div className="bg-white border border-[var(--miro-border)] rounded-xl overflow-x-auto">
        <table data-testid="access-requests-table" className="w-full text-sm">
          <thead className="bg-[var(--miro-bg)] text-[var(--miro-text-secondary)]">
            <tr className="text-left">
              <th className="px-4 py-3 font-semibold">Requester</th>
              <th className="px-4 py-3 font-semibold">Role</th>
              <th className="px-4 py-3 font-semibold">Message</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Received</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-[var(--miro-text-muted)]">
                {loading ? "Loading…" : `No ${tab} requests.`}
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`ar-row-${r.id}`} className="border-t border-[var(--miro-border)] align-top">
                <td className="px-4 py-3">
                  <div className="font-semibold text-[var(--miro-navy)]">{r.name}</div>
                  <div className="text-xs text-[var(--miro-text-muted)]">{r.email}</div>
                  {r.phone && <div className="text-xs text-[var(--miro-text-muted)]">{r.phone}</div>}
                </td>
                <td className="px-4 py-3 text-[var(--miro-text-secondary)]">
                  {r.role_requested || <span className="text-[var(--miro-text-muted)]">—</span>}
                </td>
                <td className="px-4 py-3 text-[var(--miro-text-secondary)] max-w-xs">
                  <div className="whitespace-pre-wrap line-clamp-3">
                    {r.reason || r.message || "—"}
                  </div>
                  {(r.job_title || r.department || r.division) && (
                    <div className="text-[10px] text-[var(--miro-text-muted)] mt-1">
                      {[r.job_title, r.department, r.division].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded ${STATUS_BADGE[r.status] || "bg-slate-100 text-slate-700"}`}>
                    {r.status.replace(/_/g, " ")}
                  </span>
                  {r.email_verified && r.status !== "email_verification_pending" && (
                    <div className="text-[10px] text-green-700 mt-1">Email verified</div>
                  )}
                  {r.review_notes && (
                    <div className="text-[10px] text-[var(--miro-text-muted)] mt-1 max-w-[200px] whitespace-pre-wrap">
                      Note: {r.review_notes}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-[var(--miro-text-secondary)]">
                  {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    {(r.status === "pending_approval" || r.status === "pending") && (
                      <>
                        <Button data-testid={`ar-approve-${r.id}`} size="sm"
                                style={{ background: "var(--miro-navy)" }}
                                onClick={() => setApproveTarget(r)}>
                          <CheckCircle2 size={13} className="mr-1"/> Approve
                        </Button>
                        <Button data-testid={`ar-reject-${r.id}`} size="sm" variant="outline"
                                onClick={() => setRejectTarget(r)}>
                          <XCircle size={13} className="mr-1"/> Reject
                        </Button>
                      </>
                    )}
                    {r.status === "email_verification_pending" && (
                      <span className="text-[11px] text-[var(--miro-text-muted)]">
                        Waiting for applicant to verify email
                      </span>
                    )}
                    {r.status !== "archived" && (
                      <Button data-testid={`ar-archive-${r.id}`} size="sm" variant="outline"
                              onClick={() => archive(r)}>
                        <Archive size={13} className="mr-1"/> Archive
                      </Button>
                    )}
                    <Button data-testid={`ar-remove-${r.id}`} size="sm" variant="outline"
                            className="border-red-300 text-red-700 hover:bg-red-50"
                            onClick={() => remove(r)} title="Delete request">
                      <Trash2 size={13} className="mr-1"/> Remove
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ApproveDialog
        key={approveTarget?.id || "approve-closed"}
        target={approveTarget}
        onClose={() => setApproveTarget(null)}
        onDone={load}
      />
      <RejectDialog
        key={rejectTarget?.id || "reject-closed"}
        target={rejectTarget}
        onClose={() => setRejectTarget(null)}
        onDone={load}
      />
    </AppLayout>
  );
}

const PRIVILEGED_ROLE_VALUES = ["super_admin", "founder", "hr_admin", "ops_manager", "franchise_manager"];

function ApproveDialog({ target, onClose, onDone }) {
  // Initialise state directly from target — because parent uses `key={target.id}`
  // this component fully remounts whenever the admin picks a different request,
  // so useState initialisers are the correct place to seed values.
  const [role, setRole] = useState("employee");
  const [name, setName] = useState(target?.name || "");
  const [tempPwd, setTempPwd] = useState("");
  const [employeeId, setEmployeeId] = useState(target?.employee_id || "");
  const [jobTitle, setJobTitle] = useState(target?.job_title || "");
  const [department, setDepartment] = useState(target?.department || "");
  const [division, setDivision] = useState(target?.division || "");
  const [manager, setManager] = useState(target?.reporting_manager || "");
  const [confirmPriv, setConfirmPriv] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!target) return null;
  const isPrivileged = PRIVILEGED_ROLE_VALUES.includes(role);

  const submit = async () => {
    if (isPrivileged && !confirmPriv) {
      toast.error("Confirm you want to grant a privileged role.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/access-requests/${target.id}/approve`, {
        role, name: name || undefined,
        temp_password: tempPwd.trim() ? tempPwd.trim() : undefined,
        employee_id: employeeId.trim() || undefined,
        job_title: jobTitle.trim() || undefined,
        department: department.trim() || undefined,
        division: division || undefined,
        reporting_manager: manager.trim() || undefined,
        confirm_privileged: isPrivileged ? true : undefined,
      });
      if (r.data?.email_sent) {
        toast.success(
          r.data?.used_stored_password
            ? "Approved — activation email sent to the user."
            : "Approved — credentials emailed to the user."
        );
      } else {
        toast.success("Approved. Email service not configured — share credentials manually.");
      }
      onDone?.(); onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[var(--miro-navy)]">
            Approve access request
          </DialogTitle>
          <DialogDescription className="text-sm text-[var(--miro-text-secondary)]">
            Assign a final role, division and employment context. The account
            activates immediately after approval.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
          <div className="text-sm text-[var(--miro-text-secondary)]">
            Activating a MIRO One account for
            <strong className="text-[var(--miro-navy)]"> {target.email}</strong>.
          </div>
          {target.has_password && (
            <div data-testid="ar-approve-user-pwd-hint"
                 className="text-xs text-green-700 bg-green-50 border border-green-200 rounded p-2">
              The applicant chose their own password at signup — we'll activate
              the account with that password. They'll still be prompted to
              confirm/change it on first login.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Display name</Label>
              <Input data-testid="ar-approve-name" className="mt-1"
                     value={name} onChange={(e) => setName(e.target.value)}/>
            </div>
            <div>
              <Label>Employee ID</Label>
              <Input data-testid="ar-approve-employee-id" className="mt-1"
                     value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
                     placeholder="MIRO-EMP-…"/>
            </div>
            <div>
              <Label>Job title</Label>
              <Input data-testid="ar-approve-job-title" className="mt-1"
                     value={jobTitle} onChange={(e) => setJobTitle(e.target.value)}/>
            </div>
            <div>
              <Label>Department</Label>
              <Input data-testid="ar-approve-department" className="mt-1"
                     value={department} onChange={(e) => setDepartment(e.target.value)}/>
            </div>
            <div>
              <Label>Division</Label>
              <Select value={division} onValueChange={setDivision}>
                <SelectTrigger data-testid="ar-approve-division" className="mt-1">
                  <SelectValue placeholder="Select"/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="academy">Academy</SelectItem>
                  <SelectItem value="study_abroad">Study Abroad</SelectItem>
                  <SelectItem value="mbbs_abroad">MBBS Abroad</SelectItem>
                  <SelectItem value="wealth">Wealth</SelectItem>
                  <SelectItem value="digital">Digital</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reporting manager</Label>
              <Input data-testid="ar-approve-manager" className="mt-1"
                     value={manager} onChange={(e) => setManager(e.target.value)}/>
            </div>
          </div>
          <div>
            <Label>Role</Label>
            <Select value={role} onValueChange={(v) => { setRole(v); setConfirmPriv(false); }}>
              <SelectTrigger data-testid="ar-approve-role" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
            {target.role_requested && (
              <div className="text-[11px] text-[var(--miro-text-muted)] mt-1">
                Applicant requested: <em>{target.role_requested}</em>
              </div>
            )}
            {isPrivileged && (
              <label className="mt-2 flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
                <input data-testid="ar-approve-confirm-priv" type="checkbox"
                       checked={confirmPriv} onChange={(e) => setConfirmPriv(e.target.checked)}
                       className="mt-0.5"/>
                <span>
                  I confirm I want to grant the <strong>privileged</strong>{" "}
                  role <em>{role}</em>. This grants elevated permissions.
                </span>
              </label>
            )}
          </div>
          {!target.has_password && (
            <div>
              <Label>Temporary password (optional)</Label>
              <Input data-testid="ar-approve-pwd" className="mt-1"
                     value={tempPwd} onChange={(e) => setTempPwd(e.target.value)}
                     placeholder="Leave blank to auto-generate a strong password"/>
              <div className="text-[11px] text-[var(--miro-text-muted)] mt-1">
                User will be required to change it on first login.
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="ar-approve-submit"
                  disabled={saving || !name.trim() || (isPrivileged && !confirmPriv)}
                  onClick={submit}
                  style={{ background: "var(--miro-navy)" }}>
            {saving ? "Approving…" : "Approve & activate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ target, onClose, onDone }) {
  // Parent keys this component by target.id, so useState initialisers get a
  // fresh value whenever the admin selects a different request.
  const [reason, setReason] = useState("");
  const [applicantMsg, setApplicantMsg] = useState("");
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);

  if (!target) return null;
  const canSubmit = reason.trim().length >= 3 && !saving;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/access-requests/${target.id}/reject`, {
        reason: reason.trim(),
        applicant_message: applicantMsg.trim() || null,
        notify,
      });
      toast.success("Request rejected");
      onDone?.(); onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[var(--miro-navy)]">
            Reject access request
          </DialogTitle>
          <DialogDescription className="text-sm text-[var(--miro-text-secondary)]">
            Record an internal reason. Only the applicant message (if any) is shared with the requester.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-[var(--miro-text-secondary)]">
            {target.name} · {target.email}
          </div>
          <div>
            <Label>Internal reason (required, audit only) *</Label>
            <Textarea data-testid="ar-reject-reason" rows={3} className="mt-1"
                      value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="Why is this being declined? Kept private for the audit log."/>
            {reason && reason.trim().length < 3 && (
              <div className="text-[11px] text-red-600 mt-1">Please write a short reason.</div>
            )}
          </div>
          <div>
            <Label>Applicant message (optional, shown to requester)</Label>
            <Textarea data-testid="ar-reject-applicant-msg" rows={2} className="mt-1"
                      value={applicantMsg} onChange={(e) => setApplicantMsg(e.target.value)}
                      placeholder="Neutral message like 'Please contact HR to proceed.' Leave blank for a generic notice."/>
          </div>
          <label className="flex items-center gap-2 text-xs text-[var(--miro-text-secondary)]">
            <input data-testid="ar-reject-notify" type="checkbox"
                   checked={notify} onChange={(e) => setNotify(e.target.checked)}/>
            Email the requester a polite decline.
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="ar-reject-submit"
                  disabled={!canSubmit}
                  onClick={submit}
                  style={{ background: "var(--miro-navy)" }}>
            {saving ? "Rejecting…" : "Reject request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* end */
